import StringUtility from 'utils/stringReplacement';

const copy = new StringUtility({
  title: 'Terms and Conditions',
  content: `
  **1. DEFINITIONS**

  1.A. **“Anonymized & Learnings Data”** means any Client Data which is anonymized, combined,
  aggregated, adapted or otherwise enriched (wholly or in part) with other data or information
  (including FanAI Data) of FanAI such that the Client Data (or any other data in the aggregated
  data set) no longer identifies an individual. For the avoidance of doubt, Anonymized &
  Learnings Data includes the learnings generated by the FanAI Platform and underlying
  algorithms and any anonymized data that is automatically stored by the FanAI Platform in the
  course of providing the FanAI Services.

  1.B. **“Authorized User”** means an employee or contractor of Client who is authorized to access and
  use the FanAI Platform as described in Section 2.B.

  1.C. **“Background IP”** means all Intellectual Property Rights and technology of a Party and its
  Affiliates that is created, invented, or developed prior to the Effective Date of the Agreement
  (and any improvements made, at any time, to such Background IP by either Party).

  1.D. **“Enriched Data”** means any Client Data which is combined, aggregated, adapted, or otherwise
  enriched (wholly or in part) with other data or information (including FanAI Data), that may
  include Personal Information.

  1.E. **“FanAI Data”** means all data or information owned by FanAI or any of its Affiliates or licensed
  by FanAI of any of its Affiliates from a third party, including any data or information which is
  sourced by FanAI independent of its ongoing work and involvement with the FanAI Platform,
  but excluding any Client Data.

  1.F. **“FanAI IPR”** means the Intellectual Property Rights owned by FanAI or its Affiliates, including
  all Intellectual Property Rights in the FanAI Platform and all documents, products and materials
  developed by FanAI or its agents, contractors and employees as part of or in relation to the
  FanAI Services in any form, including without limitation any source code, object code,
  algorithms, data, databases, data schemas, APIs, reports, specifications and roadmaps
  (including drafts).

  1.G. **“FanAI Platform Services”** means FanAI’s provision of the FanAI Platform and its associated
  functionality and the provision of any Reports.

  1.H. **“FanAI Platform”** means the fan insight and audience monetization platform developed by
  FanAI, or such other platforms and/or services as are procured/used by FanAI in order to
  deliver the FanAI Services or otherwise within the FanAI System. The functionality of the FanAI
  Platform is described in Schedule 2 (FanAI Platform Functionality).

  1.I. **“FanAI Services”** means the FanAI Platform Services and the FanAI Support Services.

  1.J. **“FanAI System”** means the system comprising the FanAI Platform, Reports and the Anonymized
  & Learnings Data, and all FanAI IPR therein.

  1.K. **“FanAI Support Services”** means the support services provided by FanAI following the Initial
  Pilot Period, if any, as further described in Schedule 1 (FanAI Support Services).

  1.L. **“Manipulated Data”** means the Anonymized & Learnings Data and the Enriched Data .

  1.M. **“Report”** means the industry, audience or other reports, white papers or other materials that
  FanAI produces from time to time, and which are made available for download by Client
  through the FanAI Platform. For the avoidance of doubt, the following items are not considered
  to be a Report: (i) reports, white papers or other similar materials which are made available
  for download through other media, including the FanAI website; (ii) Deliverables created for
  Client pursuant to an SOW or Order; or (iii) any materials or deliverables created by or on
  behalf of FanAI for any third party.

  **2. FANAI SERVICES**

  2.A. <u>FanAI Platform</u>. Subject to the terms and conditions of this Agreement, during the Term, FanAI
  will make available to Client and its Authorized Users, on a hosted “as-a-service” basis, the
  FanAI Platform and its associated functionality for use in processing Client Data to create
  Manipulated Data, solely for use in connection with the promotion of Client’s products and
  services.

  2.B. <u>User Accounts</u>. FanAI will establish a user account on the FanAI Platform for each of Client’s
  Authorized Users. This user account will enable such Authorized Users to access and use the
  FanAI Platform. Client will inform each Authorized User of the applicable limitations and
  restrictions set forth in this Agreement, and require each Authorized User to comply with such
  terms. Client will not permit its personnel other than Authorized Users to access or use the
  FanAI Platform, and will not disclose any user names or passwords for the FanAI Platform other
  than as necessary to enable Authorized Users to access and use the FanAI Platform. Client will
  notify FanAI immediately if Client knows of or has reason to suspect any unauthorized use of
  the FanAI Platform or any loss or theft of any user name or password. Client will be responsible
  for all activities of its Authorized Users in connection with the FanAI Platform and all activity
  using any user names or passwords issued to Client or any Authorized Users.

  2.C. <u>Relationship Data</u>. FanAI may collect certain information during the registration, activation and
  maintenance of a user account (**“Relationship Data”**), such as the names and contact details of
  Authorized Users or personnel involved in maintaining access to or using the FanAI Services.
  FanAI uses Relationship Data to manage user accounts, send notifications, deliver services or
  information, bill for purchased services, improve services, monitor compliance and provide
  support and comply with applicable law, and as otherwise provided in the FanAI Privacy Policy
  or agreed by the Parties from time to time.

  2.D. <u>Support Services</u>. Subject to the terms and conditions of this Agreement, following the Initial
  Pilot Period, FanAI may agree to provide the FanAI Support Services to Client, the details of
  which, if applicable, may be agreed by the Parties and set forth in Schedule 1 (FanAI Support
  Services).

  2.E. <u>Reports</u>. During the Term, FanAI may, from time to time, create certain publically available
  Reports. Client’s rights, during and after the Term, to use Reports are set forth in Section 3.B.
  1(c).

  2.F. <u>Restrictions</u>. Without limiting any other provision of this Agreement, Client will not (and will
  not permit any Authorized User or third party to): (a) access or use the FanAI System or any
  portion thereof for any purpose or in any manner other than as expressly permitted under this
  Agreement; (b) attempt or purport to sell, lease, sublicense, assign, or transfer access to the
  FanAI System or any portion thereof to any third party, other than providing access to
  Authorized Users as expressly permitted under this Agreement; (c) extract ideas, algorithms,
  procedures, workflows or hierarchies from the FanAI System or any portion thereof, or
  otherwise attempt to reverse engineer the FanAI System or any portion thereof, or use the
  FanAI System or any portion thereof for the purpose of creating or contributing to another
  product or service; (d) provide any means of access to, or otherwise display or transmit, the
  FanAI System from or through any website (including by “framing” or “mirroring”) other than
  the website provided by FanAI for such purposes; (e) remove any disclaimer, copyright,
  trademark, confidentiality, or other legal notice from the FanAI System or any portion thereof
  (or any associated documentation or materials); (f) use the FanAI System or any portion thereof
  for any fraudulent or unlawful purpose or in any manner that would violate the legal rights of
  any third party; (g) interfere with or disrupt the FanAI System or the servers, software or
  networks used to make the FanAI System available; (h) upload to or process using the FanAI
  Platform any (i) content or materials that Client does not have the right to upload and process,
  or that would otherwise infringe, misappropriate, or violate any third party’s rights or, (ii)
  content or other materials containing any virus, worm, Trojan horse, time bomb, spyware,
  malware or other harmful or potentially harmful code; (iii) Social Security numbers, driver’s
  license numbers, financial account numbers (iv) Personal Information about children under the
  age of 13; or (v) any other information that is deemed sensitive under applicable laws or
  regulations. FanAI will have the unrestricted right, but not the obligation, to monitor Client’s
  and Authorized Users’ use of the FanAI System and all portions thereof. Client will, and will
  cause each Authorized User to, use the FanAI System and all portions thereof in accordance
  with all applicable laws and regulations. FanAI shall have the right to limit the permitted
  calls to the FanAI System (the “Call Threshold”) in FanAI’s discretion and may give you notice
  thereof. FanAI reserves the right to suspend or terminate access immediately in the event of
  any violation of the license restrictions or any other determination that use of the FanAI
  Systems is causing or threatens to cause harm or liability to the FanAI System or FanAI.

  2.G. <u>Post Termination Data</u>. FanAI will promptly destroy any Client Data that it has obtained from
  Client in connection with the FanAI Services and all Enriched Data (but, for clarity, may
  continue to retain (i) all FanAI Data and (ii) Anonymized & Learnings Data); and, subject to
  Client’s ongoing rights to the Reports (and the Manipulated Data therein) as set forth in 3.B.1c,
  Client will promptly return to FanAI all FanAI IPR, FanAI Data and destroy all Manipulated Data
  (but excluding any Client Data or the Reports) supplied in connection with this Order that
  Client has in its possession or control.

  **3. FURTHER INTELLECTUAL PROPERTY RIGHTS**

  3.A. Ownership

  - 3.A.1. As between the Parties, FanAI exclusively owns and retains all right, title and interest
  (including all Intellectual Property Rights) in and to:
  (a) the FanAI System (including all
  Anonymized & Learnings Data) and all portions thereof; (b) the FanAI Data; (c) all FanAI
  IPR; (d) technical data generated from access to or use of the FanAI Services, such as
  access records, date and time stamps, transaction and activity records, and system
  performance data (**“Usage Data”**), and (e) any Intellectual Property Rights developed
  by FanAI or its Affiliates, contractors, consultants or outsourcing providers, whether
  related to this Agreement or not, during the Term (“FanAI Developments”); in each
  case of (a) to (e), including all improvements, enhancements or modifications thereto
  made by or on behalf of FanAI;

  - 3.A.2. Subject to the rights expressly granted under this Agreement and the restrictions upon
  use and destruction requirements as set forth herein, as between the Parties, the
  Parties jointly own the Enriched Data.

  - 3.A.3. The entire right title and interest in any FanAI Developments will vest in FanAI upon
  creation of such FanAI Developments, and in the event Client is deemed to obtain any
  interest in any FanAI Developments, Client will assign, without further consideration,
  all of its right, title, and interest in and to such FanAI Developments and Intellectual
  Property Rights therein to FanAI.


  3.B. Licenses

  - 3.B.1. <u>FanAI License Grant</u>. Subject to the terms and conditions of this Agreement (including
  satisfaction of all payment provisions) during the Term of this Agreement, FanAI hereby
  grants to Client (and its Authorized Users):

    - a) a revocable, non-exclusive, non-transferable, non-sublicensable license during the
  Term under the FanAI IPR solely for the purposes of receiving and using the FanAI
  Services to use and to promote Client’s products and services. The foregoing license is
  subject to the following restrictions: (i) subject to Client’s rights to use the
  Manipulated Data in Section 3.B.1(c) below, Client will use the FanAI System for its
  internal purposes only, and in no event will the FanAI System be disclosed, made
  available to or used for the benefit of any third party, sold, assigned, leased or
  otherwise disposed of, or commercially exploited or marketed in any way, with or
  without charge, by or on behalf of Client; (ii) except to the extent permitted by
  applicable law, Client will not copy, modify, translate, decompile, disassemble or
  otherwise reverse engineer, or otherwise determine or attempt to determine source
  code or protocols from, the executable code of the FanAI System, or create any
  derivative works based upon the FanAI System, and Client will not permit or authorize
  anyone else to do so. Client also agrees that any works created in violation of this
  subsection are derivative works, and as such, Client assigns all right, title and interest
  therein to FanAI;

    - b) a non-exclusive, non-transferable, non-sublicensable license during the Term to use any
  FanAI Data that is provided by FanAI under this Agreement, if any, solely for the
  purpose of receiving and using the FanAI Services to use and to promote Client’s
  products and services during the Term of the Agreement. For the avoidance of doubt,
  other than as expressly permitted in Section 3.B.1(c) below, Client will not disclose or
  make available the FanAI Data to, or use the FanAI Data for the benefit of, any third
  party; and

    - c) a non-exclusive, non-transferable license to store, access, view, use, copy and analyze
  the Manipulated Data to use and to promote Client’s products and services during the
  Term. For the avoidance of doubt, Client will not disclose or make available the
  Manipulated Data to or use the Manipulated Data for the benefit of any third party,
  other than the Manipulated Data provided by FanAI in the form of the FanAI Platform,
  and any Reports that Client receives, during the Term. Following the expiration of the
  Term or termination of this Agreement, Client will have the perpetual, fully paid-up
  right to continue to use the Reports for any purpose; however, Client’s right to use the
  Manipulated Data in such Reports will be (i) subject to FanAI’s IP rights therein, and (ii)
  limited to the extent necessary to use the Reports, and Client will not be entitled to
  retain or access any other FanAI Data or Enriched Data of FanAI, which contains the
  confidential and proprietary information of FanAI and its licensors.


  - 3.B.2. <u>Client License Grant</u>. Subject to the terms and conditions of this Agreement, Client
  hereby grants to FanAI:

    - a) a worldwide, non-exclusive, non-transferable, royalty-free license under Client’s rights
  in the Client Data to (i) store the Client Data; (ii) access, view, copy, analyze and
  modify the Client Data to generate the Manipulated Data; and (iii) otherwise use the
  Client Data in connection with providing the FanAI System to Client under this
  Agreement and for the purposes of improving the FanAI Services;

    - b) a worldwide, non-exclusive, non-transferable, royalty-free license during the Term
  under Client’s rights in the Enriched Data to access, view, copy, analyze, modify and
  otherwise use the Enriched Data to provide the FanAI Services to Client under this
  Agreement and for the purposes of improving the FanAI Services.


  4\\. <u>Survival</u>. The terms of Sections 2.C, 2.F and 3 and any terms which by their nature are
  intended or ought to survive the expiration or termination of this Order will survive the
  expiration or termination of this Order.
  `,
});

export default copy;
